/**
 * APPLICATION MAIN ENTRY POINT
 *
 * This script includes the globals, store, components and router that are
 * needed to get the app running.
 *
 * @version  0.1
 */

import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { Router, Route, hashHistory, IndexRedirect} from 'react-router';

// Add polyfills first
import applyAllPolyfills from './scripts/utils/polyfills';

// Add CSS
import 'c3/c3.min.css';
import './index.css';

// i18n
import { IntlProvider } from 'react-intl';

// Import utility libraries (XHR and utils)
import xhr from './scripts/utils/xhr';
import utils from './scripts/utils/utilities';
import storage from './scripts/utils/storage';

// Import Redux store
import reduxStore from './scripts/reduxStore';

// Import the popup messages actions to add
import popupActions from './scripts/reusable/MessagePopup/redux/MessagePopup.actions';

import errorMessagesActions from './scripts/reusable/MessagePopup/redux/MessagePopup.actions';

import commonActions from './scripts/api/Common.actions';

import api from './scripts/pages/LoginPage/LoginPage.api';

// Import components
import Main from './scripts/pages/Main/Main.index.jsx';
import PreviewCompany from './scripts/pages/PreviewCompany/PreviewCompany.index.jsx';
import UploadWizard from './scripts/pages/BatchUpload/BatchUpload.index.jsx';
import ErrorPage from './scripts/pages/ErrorPage/ErrorPage.index.jsx';
import TableTestPage from './scripts/pages/TableTestPage/TableTestPage.index.jsx';
import LogoutPage from './scripts/pages/LogoutPage/LogoutPage.index';
import StartPage from './scripts/pages/StartPage/StartPage.index.jsx';
import { SanctionsListPage, SanctionReportsPage } from './scripts/containers/SanctionsWarnings/SanctionsWarnings.containers';
import TrendingTermsListingPage from './scripts/pages/TrendingTerms/TrendingTermsListingPage';
import TrendingEntitiesListingPage from './scripts/pages/TrendingEntities/TrendingEntitiesListingPage';
import NewsListingPage from './scripts/pages/News/NewsListingPage';
import AddRssFeed from './scripts/pages/RSSEditing/AddRssFeed/AddRssFeed.index.jsx';
import EditFeed from './scripts/pages/RSSEditing/EditFeed/EditFeed.index.jsx';
import ViewFeedContent from './scripts/pages/ViewFeedContent/ViewFeedContent.index.jsx';
import FeedListing from './scripts/pages/FeedListing/FeedListing';
import EditThirdParty from './scripts/pages/EditThirdParty/EditThirdParty';
import CreateNewEntity from './scripts/pages/CreateNewEntity/CreateNewEntity';
import ViewArticlePage from './scripts/pages/ViewArticlePage/ViewArticlePage.jsx';
import EntityDashboard from './scripts/pages/EntityDashboard/EntityDashboard.jsx';
import ExperianFullDoc from './scripts/pages/ExperianFullDoc/ExperianFullDoc.jsx';
import EmailAlertsPage from './scripts/pages/EmailAlerts/EmailAlertsPage.jsx';
import CreateAlertPage from './scripts/pages/AlertsCreate/CreateAlertPage';
import EditAlertPage from './scripts/pages/AlertsEdit/AlertsEditPage';
import ManageCategoriesPage from './scripts/pages/Categories/ManageCategoriesPage';
import EditCategoryPage from './scripts/pages/Categories/EditCategoryPage';
import CreateCategoryPage from './scripts/pages/Categories/CreateCategoryPage';
import AccountUsage from './scripts/pages/AccountUsage/AccountUsage';
import UnsubscribePage from './scripts/pages/UnsubscribePage/UnsubscribePage';
import EntitiesNewsListingPage from './scripts/pages/EntitiesNews/EntitiesNewsListingPage';
//import Entities

// Internationalization imports and settings
import { LOCALES, DEFAULT_LANGUAGE, UM_ERROR_TYPES } from './scripts/constants';
import EntityListing from "./scripts/pages/Entities/EntityListing";
import InactiveEntitiesListing from "./scripts/pages/Entities/InactiveEntitiesListing";

import { routeCheckPermission } from "./scripts/permissions";
import {pendoPositionController} from "./scripts/utils/pendoPositionController";


// Apply all necessary polyfills for the app to work correctly
applyAllPolyfills();

let previousLanguage = DEFAULT_LANGUAGE;
let pendingLoadPrefs = false;
let loadedGenericMeta = false;
// This array is used to store resumeApp() calls in the order they should be called. The loadPrefs() function is called
// twice by the React router and in both cases we need to call resumeApp() so that the app starts. Unfortunately, all
// resumeApp calls need to be called after api.getHelpUrlRoot() is resolved.
let resumeAppCalls = [];

let intlTranslations = LOCALES;

/**
 * Get specific data for a certain locale (id, locale or translations).
 *
 * @param   {string}        key   The property to be fetched.
 * @return  {string|Object}       The data from the specified property.
 */
function getLocale(key) {
    let preferences = reduxStore.getState().user.preferences,
        language = preferences && preferences.language ? preferences.language : DEFAULT_LANGUAGE;

    if (language) {
        return intlTranslations[language][key];
    }
}

function loadGenericMeta(nextState, redirectRoute, resumeApp) {

    if (loadedGenericMeta) {
        resumeApp();
    } else {
        Promise.all(commonActions.loadPageMeta())
            .then(()=>{
                loadedGenericMeta = true;
                resumeApp();
            })
            .catch((error) => {
                utils.logError(error, 'index.genericPageMeta(): ');
                // xhr.handleAllErrors(error);
                resumeApp();
            });
    }
}

/**
 * This function is used to consume resumeApp() calls in the order they are
 * stored and at a certain moment in time, not at each loadPrefs() call.
 *
 * @return  nothing
 */
function consumeResumeAppCalls() {
    for (let i = 0; i < resumeAppCalls.length; i++) {
        resumeAppCalls[i]();
    }
}


function handlePrefsPermissions(nextState, redirectRoute, resumeApp) {
    loadPrefs(nextState, redirectRoute, () => {
        routeCheckPermission({}, nextState, redirectRoute);
        resumeApp();
    });
}

function loadPrefs(nextState, redirectRoute, resumeApp) {
    if (pendingLoadPrefs) {
        resumeAppCalls.push(resumeApp);
        return;
    }

    pendingLoadPrefs = true;

    resumeAppCalls.push(resumeApp);

    if (loadedGenericMeta) {
        Promise.all(commonActions.loadPageProfile())
            .then(() => {
                pendingLoadPrefs = false;
                consumeResumeAppCalls();
            })
            .catch((error) => {
                utils.logError(error, 'index.loadPrefs(): ');
                pendingLoadPrefs = false;
                xhr.handleAllErrors(error);
                // resumeApp();
                consumeResumeAppCalls();
            });
    } else {
        Promise.all(commonActions.loadPageMeta())
            .then(()=>{
                loadedGenericMeta = true;
            })
            .then(() => {
                return Promise.all(commonActions.loadPageProfile())
            })
            .then(() => {
                pendingLoadPrefs = false;
                consumeResumeAppCalls();
            })
            .catch((error) => {
                utils.logError(error, 'index.loadPrefs(): ');
                pendingLoadPrefs = false;
                xhr.handleAllErrors(error);
                // resumeApp();
                consumeResumeAppCalls();
            });
    }
}

function loadNotFoundPageData(nextState, redirectRoute, resumeApp) {
    popupActions.setMessages({
        messages: [
            {
                titleId:     'General_CoreFunctionality_Error_error.404Title',
                messageType: 'system-error',
                messageId:   'General_CoreFunctionality_Error_error.404Message'
            }
        ]
    });

    loadGenericMeta(nextState, redirectRoute, resumeApp);
}

/**
 * This function injects the JS dependencies in the head section of the document.
 *
 * @callback  scriptOnLoadCallback
 *
 * @param     {string}                 script The location of the JS file.
 * @param     {scriptOnLoadCallback}   scriptOnLoadCallback callback that will get executed after the script loads.
 * @return    nothing
 */
function loadJs(script, scriptOnLoadCallback) {
    var scr = document.createElement("script");
    scr.type="text/javascript";
    scr.language="javascript";
    scr.addEventListener('load', scriptOnLoadCallback);
    scr.src=script;
    document.getElementsByTagName('head')[0].appendChild(scr);
}

/**
 * This component is used to wrap translations from <FormattedMessage> and other React Intl components.
 *
 * @param   {Array<ReactElement>} children   The text node that needs to be rendered inside.
 * @return  {HTMLElement}
 */
function ReactIntlTextComponent({children}) {
    return (<span className="notranslate formatted-message">{children}</span>);
}

function loadRouter() {

    // Routing system
    ReactDOM.render(
        (
            <Provider store={reduxStore}>
                <IntlProvider locale={getLocale('locale')}
                              messages={getLocale('translations')}
                              textComponent={ReactIntlTextComponent}>
                    <Router history={hashHistory}>
                        <Route path="/" component={Main} onEnter={handlePrefsPermissions} onChange={routeCheckPermission}>
                            <IndexRedirect to="/start"/>
                            <Route path="/start" component={StartPage} />
                            <Route path="/sanctions-reports/report/:index" component={SanctionReportsPage} />
                            <Route path="/sanctions-reports/old-company/:companyId" component={SanctionsListPage} />
                            <Route path="/sanctions-reports/company/:companyId" component={SanctionsListPage} />
                            <Route path="/trending-terms/:term/:filters" component={TrendingTermsListingPage} />
                            <Route path="/trending-entities/:entityId/:riskAngle/:filters" component={TrendingEntitiesListingPage} />
                            <Route path="/news/:pestle/:date/:params" component={NewsListingPage} />
                            <Route path="/entity-articles" component={EntitiesNewsListingPage} />
                            <Route path="/upload" component={UploadWizard} />
                            <Route path="/review-companies" component={InactiveEntitiesListing} />
                            <Route path="/preview-company/:companyId" component={PreviewCompany} />
                            <Route path="/preview-company/:companyId/:articleId/:articleIndex/:backRoute" component={PreviewCompany} />
                            <Route path="/feeds" component={FeedListing} />
                            <Route path="/add-new-feed" component={AddRssFeed} />
                            <Route path="/manage-third-parties" component={EntityListing} />
                            <Route path="/test-table" component={TableTestPage} />
                            <Route path="/edit-third-party/:companyId" component={EditThirdParty} />
                            <Route path="/create-new-entity" component={CreateNewEntity}/>
                            <Route path="/edit-feed/:feedId" component={EditFeed} />
                            <Route path="/view-feed-content/:feedId" component={ViewFeedContent} />
                            <Route path="/create-new-alert" component={CreateAlertPage} />
                            <Route path="/view-article/:articleId/:articleIndex" component={ViewArticlePage} />
                            <Route path="/entity-dashboard/:companyId" component={EntityDashboard} />
                            <Route path="/view-experian/:companyId/:experianId" component={ExperianFullDoc} />
                            <Route path="/email-alerts" component={EmailAlertsPage} />
                            <Route path="/email-alerts/:alertId" component={EditAlertPage} />
                            <Route path="/categories" component={ManageCategoriesPage} />
                            <Route path="/categories/:categoryId" component={EditCategoryPage} />
                            <Route path="/create-category" component={CreateCategoryPage}/>
                            <Route path="/account" component={AccountUsage} />
                        </Route>
                        <Route path="/logout" component={LogoutPage} />
                        <Route path="/unsubscribe" component={UnsubscribePage} />
                        <Route path="/error" component={ErrorPage} onEnter={loadGenericMeta} />
                        <Route path="*" component={ErrorPage} onEnter={loadNotFoundPageData} />
                    </Router>
                </IntlProvider>
            </Provider>
        ),

        document.getElementById('page-wrapper')
    );

}

function loadRouterWithLanguageRefresh() {
    buildOneTrustScript();
    const { pendo } = window;
    
    if (pendo && pendo.isReady) {
        pendo.initialize({
            visitor: {
                id: 'visitor'
            },
            account: {},

            events: {
                guidesLoaded: function () {
                    const resourceCenterGuide = pendo.findGuideBy('launchMethod', 'badge');
                    const placeBadgeMethod = resourceCenterGuide && resourceCenterGuide.placeBadge;

                    if (placeBadgeMethod) {
                        resourceCenterGuide.placeBadge = function () {
                            const returnValue = placeBadgeMethod();
                            const badgeElement =
                                pendo.badgesShown &&
                                pendo.badgesShown[resourceCenterGuide.id] &&
                                pendo.badgesShown[resourceCenterGuide.id].element &&
                                pendo.badgesShown[resourceCenterGuide.id].element();

                            if (badgeElement) {
                                const pendoElement = document.getElementsByClassName("_pendo-resource-center-badge-container")[0];
                                const footerElement = document.getElementsByClassName("footer__wrapper")[0];
                                pendoPositionController(pendoElement, footerElement);
                                window.onscroll = () => pendoPositionController(pendoElement, footerElement);
                                window.onresize = () => pendoPositionController(pendoElement, footerElement);
                            }
                            return returnValue;
                        }
                    }
                }
            }
        });
    }

    loadRouter();

    reduxStore.subscribe(() => {
        let currentLanguage = reduxStore.getState().user.preferences.language;

        if (currentLanguage !== previousLanguage) {
            previousLanguage = currentLanguage;
            loadRouter();
        }
    });
}

/**
 * On completion of page load
 *
 * @return  nothing
 */

/**
 * On completion of page load, request Net Insight connection information and then mount the router.
 *
 * @return  nothing
 */

function buildErrorPage(error) {
    document.getElementById("page-wrapper").innerHTML =
        '<div class="application-config-fetch-error">' +
        'Application failed to start. Unable to fetch configuration data.' +
        '</div>';
    utils.logError(error, 'index.window.onload');
}

function buildOneTrustScript() {
    const data_domain = document.createElement("script");
    data_domain.setAttribute('type', "text/javascript");
    data_domain.setAttribute('src', "https://cdn.cookielaw.org/scripttemplates/otSDKStub.js");
    data_domain.setAttribute('charset', "UTF-8");
    data_domain.setAttribute('data-domain-script', "97158a57-49f5-48f6-a157-2fb6b4dd8465-test");

    const optanon = document.createElement("script");
    optanon.setAttribute('type', "text/javascript");
    optanon.innerHTML = 'function OptanonWrapper() { }';

    const head = document.querySelector('head');
    const title = document.querySelector('title');

    head.insertBefore(optanon, title);
    head.insertBefore(data_domain, title);
}

function onPageLoad() {
    let lna2 = utils.getUrlQueryParam("lna2");
    let clientidauthtoken = utils.getUrlQueryParam("clientidauthtoken");
    if (lna2) {
        console.log("===== with lna2", window.location.hash);
        if (window.location.hash === "#/error" ) {
            console.log("loading with refresh");
            loadRouterWithLanguageRefresh();
            return;
        }
        // if coming from WAM log in first
        storage.mergeObject("lnei", {authInfo: {type: "WAM", lna2}});
        return api
            .loginWam(window.location.search)
            .then((res) => {
                console.log("success logging in");
                let lnsi = utils.getUrlQueryParam("lnsi");
                if (lnsi) {
                    storage.mergeObject("lnei", {authInfo: {type: "WAM", lnsi, lna2}, serverInfo: res.body});
                }
                window.location.replace(window.location.protocol + "//" + window.location.host + "/#/start");
                loadRouterWithLanguageRefresh();
            })
            .catch((err) => {
                // TODO: handle error specifically (401 vr rest of the world)
                if (err.status === 401) {
                    if (err.response.body.reason === UM_ERROR_TYPES.missingCustomer) {
                        errorMessagesActions.setMessages({
                            messages: [
                                {
                                    titleId:     'General_CoreFunctionality_Error_error.FailedLoginTitle',
                                    messageType: 'system-error',
                                    messageId:   'General_CoreFunctionality_Error_error.NoCustomerMessage'
                                }
                            ]
                        });
                        hashHistory.push("/error");
                        loadRouterWithLanguageRefresh();
                        return;
                    }
                    if (err.response.body.reason === UM_ERROR_TYPES.authTypeNotAllowed) {
                        errorMessagesActions.setMessages({
                            messages: [
                                {
                                    titleId:     'General_CoreFunctionality_Error_error.FailedLoginTitle',
                                    messageType: 'system-error',
                                    messageId:   'General_CoreFunctionality_Error_error.WAMLoginNotAllowed'
                                }
                            ]
                        });
                        hashHistory.push("/error");
                        loadRouterWithLanguageRefresh();
                        return;
                    }
                    if (err.response.body.reason === UM_ERROR_TYPES.invalidLna2) {
                        let auth = storage.getObject("lnei");
                        window.location = err.response.body.wamLogout + "&redirecturl=" + encodeURIComponent(err.response.body.customerUi) + "&lnsi=" + auth.authInfo.lnsi;
                        return;
                    }
                    window.location = err.response.body.wamLogin + "?back=" + encodeURIComponent(err.response.body.customerUi) + "&aci=" + err.response.body.wamAciCode;
                } else if (err.status === 400 && err.response.body && err.response.body.status === UM_ERROR_TYPES.missingData) {
                    console.log("===========", err.response.body.authenticationInformation);
                    storage.mergeObject("lnei", {serverInfo: err.response.body.authenticationInformation});
                    errorMessagesActions.setMessages({
                        messages: [
                            {
                                titleId:     'General_CoreFunctionality_Error_error.FailedLoginTitle',
                                messageType: 'system-error',
                                messageId:   'General_CoreFunctionality_Error_error.FailedLoginMessage'
                            }
                        ]
                    });
                    hashHistory.push("/error");
                    loadRouterWithLanguageRefresh();
                } else {
                    hashHistory.push("/error");
                    loadRouterWithLanguageRefresh();
                }
            })
    } else {
        if (window.location.hash === "#/logout" ||
            window.location.hash === "#/error" ||
            window.location.hash === "#/unsubscribe") {
            console.log("====== proceed with page without ping ===== ");
            loadRouterWithLanguageRefresh();
            return;
        }

        // Check if already logged in
        return xhr
            .get({url: '/api/user/ping'})
            .then((res) => {
                console.log("====== proceed with page load ===== ");
                loadRouterWithLanguageRefresh();
            })
            .catch((err)=> {
                console.log("===== rejection", err.response.body);
                if (err.status === 401) {
                    utils.clearUserSession();
                    err.response.body.type = "WAM";
                    storage.mergeObject("lnei", {authInfo: {type: "WAM"}, serverInfo: err.response.body});

                    if (clientidauthtoken) {
                        window.location = err.response.body.wamLogin + "?back=" + encodeURIComponent(err.response.body.customerUi + "/?clientidauthtoken=" + clientidauthtoken) + "&aci=" + err.response.body.wamAciCode;
                    } else {
                        window.location = err.response.body.wamLogin + "?back=" + encodeURIComponent(err.response.body.customerUi) + "&aci=" + err.response.body.wamAciCode;
                    }
                } else {
                    buildErrorPage(err);
                }
            });
    }
}

window.addEventListener('load', onPageLoad);


// WEBPACK FOOTER //
// ./src/index.js